import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Contrato,
  CreateContratoRequest,
  UpdateContratoRequest
} from '../models/contrato.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { ContratoMapper } from '../models/contrato.mapper';
import {
  ContratoWithDetailsDto,
  CreateContratoDto,
  UpdateContratoDto,
  CreateContratoResponseDto
} from '../models/contrato.dto';

@Injectable({
  providedIn: 'root'
})
export class ContratoService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/Contrato`;

  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  /**
   * Get all contratos with details (tipo contrato and perfil names)
   */
  getContratos(): Observable<Contrato[]> {
    return this.http
      .get<ApiEnvelope<ContratoWithDetailsDto[]>>(`${this.baseUrl}/GetAllWithDetails`)
      .pipe(
        this.unwrapResponse<ContratoWithDetailsDto[]>(),
        map((res: ContratoWithDetailsDto[]) => ContratoMapper.fromDtoWithDetailsList(res))
      );
  }

  /**
   * Get a contrato by ID with details
   * @param id - Contrato ID
   */
  getContratoById(id: number): Observable<Contrato> {
    const params = new HttpParams().set('id', id.toString());

    return this.http
      .get<ApiEnvelope<ContratoWithDetailsDto>>(`${this.baseUrl}/GetByIdWithDetails`, { params })
      .pipe(
        this.unwrapResponse<ContratoWithDetailsDto>(),
        map((res: ContratoWithDetailsDto) => ContratoMapper.fromDtoWithDetails(res))
      );
  }

  /**
   * Create a new contrato
   * @param contrato - Contrato data
   */
  createContrato(contrato: CreateContratoRequest): Observable<{ id: number }> {
    const dto: CreateContratoDto = ContratoMapper.toCreateDto(contrato);

    return this.http
      .post<ApiEnvelope<CreateContratoResponseDto>>(`${this.baseUrl}/Create`, dto)
      .pipe(
        this.unwrapResponse<CreateContratoResponseDto>(),
        map((res: CreateContratoResponseDto) => ({ id: res.contrato_ID }))
      );
  }

  /**
   * Update an existing contrato
   * @param id - Contrato ID
   * @param contrato - Updated contrato data
   */
  updateContrato(id: number, contrato: UpdateContratoRequest): Observable<boolean> {
    const dto: UpdateContratoDto = ContratoMapper.toUpdateDto(contrato);

    return this.http.put<ApiEnvelope<boolean>>(`${this.baseUrl}/Update/${id}`, dto).pipe(
      this.unwrapResponse<boolean>(),
      map(() => true)
    );
  }
}
